import { ChangeDetectionStrategy, Component, ElementRef, computed, input, resource, viewChild } from '@angular/core';
import { renderAsync } from 'docx-preview';
import { ProgressSpinner } from '@openng/optimus-ui/progressspinner';
import { ButtonModule } from '@openng/optimus-ui/button';

const KNOWN_DOCX_ERROR_MESSAGES = new Set(['Document not found.', 'You do not have permission to view this document.']);

@Component({
  selector: 'lib-docx-viewer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProgressSpinner, ButtonModule],
  templateUrl: './docx-viewer.component.html',
  styleUrl: './docx-viewer.component.scss'
})
export class DocxViewerComponent {
  src = input.required<string>();
  height = input<string>('100%');

  private readonly container = viewChild.required<ElementRef<HTMLDivElement>>('docxContainer');

  #docResource = resource({
    params: () => this.src(),
    loader: async ({ params: url }) => {
      const container = this.container().nativeElement;
      container.innerHTML = '';

      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Document not found.');
        }
        if (response.status === 401 || response.status === 403) {
          throw new Error('You do not have permission to view this document.');
        }
        throw new Error(`Failed to load document: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      await renderAsync(arrayBuffer, container, undefined, {
        className: 'docx-viewer',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        breakPages: true,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true
      });
    }
  });

  isLoaded = computed(() => !this.#docResource.isLoading());
  error = computed(() => {
    const err = this.#docResource.error() as Error | undefined;
    if (!err) {
      return null;
    }
    return KNOWN_DOCX_ERROR_MESSAGES.has(err.message) ? err.message : 'Failed to load document. Please try again.';
  });

  retry(): void {
    this.#docResource.reload();
  }
}
